import {
  AlertCircle,
  CheckCircle2,
  Copy,
  MoreVertical,
  Pencil,
  Trash2,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';

import { CopyButton } from '@/components/copy-button';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';

import { useDeleteToolMutation, useUpdateToolMutation } from '@/query/tool.query';

import { Permissions } from '@/constants';

import { DATE_FORMAT, formatDate } from '@/lib/date';
import { copyToClipboard } from '@/lib/utils';

import { EditToolDialog } from './edit-tool-dialog';
import { renderToolStatusBadge, renderToolTypeBadge } from './tool-badge';

import useAlertStore from '@/store/alert.store';
import useModalStore from '@/store/modal.store';
import { useToolsStore } from '@/store/tools.store';

export function ToolHeader() {
  const tool = useToolsStore(state => state.selectedTool);
  const selectTool = useToolsStore(state => state.selectTool);
  const clearSelectedTool = useToolsStore(state => state.clearSelectedTool);
  const openModal = useModalStore(state => state.openModal);
  const { openAlert } = useAlertStore();

  const deleteMutation = useDeleteToolMutation({
    onSuccess: () => clearSelectedTool(),
  });

  const updateMutation = useUpdateToolMutation({
    onSuccess: updatedTool => selectTool(updatedTool),
  });

  if (!tool) return null;

  const handleCopyId = async () => {
    const didCopy = await copyToClipboard(tool.id);

    if (!didCopy) {
      toast.error('Failed to copy tool ID');
      return;
    }

    toast.success('Tool ID copied', {
      description: 'The tool ID has been copied to your clipboard.',
    });
  };

  const handleDelete = () => {
    openAlert({
      title: 'Delete tool',
      description: `Are you sure you want to delete "${tool.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: () => deleteMutation.mutate(tool.id),
      type: 'destructive',
    });
  };

  const handleEdit = () => {
    openModal(() => <EditToolDialog tool={tool} />);
  };

  const handleToggleEnabled = (enabled: boolean) => {
    updateMutation.mutate({
      toolId: tool.id,
      payload: { enabled },
    });
  };

  return (
    <div className="border-b bg-muted/30 px-4 py-4 sm:px-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            <Wrench className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start gap-2">
              <h2 className="text-base font-semibold leading-tight">{tool.name}</h2>
              {renderToolTypeBadge(tool.type)}
              {renderToolStatusBadge(tool.status)}
            </div>

            <p className="mt-1 text-sm leading-snug text-muted-foreground">{tool.description}</p>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <div className="flex min-w-0 items-center gap-1">
                <span className="font-medium text-foreground/60">Tool ID:</span>
                <span className="break-all font-mono text-foreground/80">{tool.id}</span>
                <CopyButton
                  value={tool.id}
                  tooltip="Copy tool ID"
                  className="h-5 w-5"
                  iconClassName="h-3 w-3"
                  onCopy={() =>
                    toast.success('Tool ID copied', {
                      description: 'The tool ID has been copied to your clipboard.',
                    })
                  }
                  disabled={updateMutation.isPending || deleteMutation.isPending}
                />
              </div>

              {tool.updated_at && (
                <span>Updated {formatDate(tool.updated_at, DATE_FORMAT.FULL)}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto lg:justify-end">
          <div className="flex items-center gap-1.5 rounded-md border bg-background/80 px-2.5 py-1.5">
            {tool.enabled ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
            )}

            <span className="text-xs text-muted-foreground">
              {tool.enabled ? 'Enabled' : 'Disabled'}
            </span>

            <ProtectedComponent requiredPermissions={[Permissions.ToolsCreateUpdate]}>
              <Switch
                checked={tool.enabled}
                onCheckedChange={handleToggleEnabled}
                disabled={updateMutation.isPending || deleteMutation.isPending}
                className="ml-0.5 scale-75"
              />
            </ProtectedComponent>
          </div>

          <Separator orientation="vertical" className="hidden h-6 lg:block" />

          <ProtectedComponent requiredPermissions={[Permissions.ToolsCreateUpdate]}>
            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-1 gap-1.5 text-xs sm:flex-none"
              onClick={handleEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          </ProtectedComponent>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCopyId}>
                <Copy className="h-4 w-4" />
                Copy tool ID
              </DropdownMenuItem>
              <ProtectedComponent requiredPermissions={[Permissions.ToolsCreateUpdate]}>
                <DropdownMenuItem onClick={handleEdit}>
                  <Pencil className="h-4 w-4" />
                  Edit tool
                </DropdownMenuItem>
              </ProtectedComponent>
              <ProtectedComponent requiredPermissions={[Permissions.ToolsDelete]}>
                <DropdownMenuItem variant="destructive" onClick={handleDelete}>
                  <Trash2 className="h-4 w-4" />
                  Delete tool
                </DropdownMenuItem>
              </ProtectedComponent>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
